import { sendMessage } from "@/utils/socket";

// 重新导出 WebSocket 连接管理功能，保持向后兼容
export { initWebSocket, closeWebSocket } from "@/utils/socket";

// 生成UUID
export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 获取聊天记录
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {number} [options.pageSize=12] - 每页大小
 * @param {number} [options.pageNum=1] - 页码
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回聊天记录
 */
export function getChatMessages({
  chatId,
  pageSize = 12,
  pageNum = 1,
  platformType = "dating",
  timestamp,
  requestId,
  msgTimestamp,
}) {
  const params = {
    events: "chat.get.messages",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      pageSize,
      pageNum,
      msgTimestamp,
    },
  };
  return sendMessage(params);
}

/**
 * 发送文本消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.text - 消息文本
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendTextMessage({
  chatId,
  text,
  platformType = "dating",
  timestamp,
  requestId,
  platformUserId,
  userId,
}) {
  const params = {
    events: "chat.post.messages",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      text,
      platformUserId,
      userId,
    },
  };
  return sendMessage(params);
}

/**
 * 发送图片消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.ossId - OSS文件ID
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendPhotoMessage({
  chatId,
  ossId,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.photos",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      text: "",
      reference: ossId,
      fileCateType: 1,
    },
  };
  return sendMessage(params);
}

/**
 * 发送视频消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.ossId - OSS文件ID
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendVideoMessage({
  chatId,
  ossId,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.videos",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      text: "",
      reference: ossId,
      fileCateType: 2,
    },
  };
  return sendMessage(params);
}

/**
 * 发送语音消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.ossId - OSS文件ID
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendAudioMessage({
  chatId,
  ossId,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.audios",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      text: "",
      reference: ossId,
      fileCateType: 3,
    },
  };
  return sendMessage(params);
}

/**
 * 发送礼物消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.reference - 礼物引用
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendGiftMessage({
  chatId,
  reference,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.cheers",
    timestamp,
    requestId: requestId,
    data: {
      reference: "cheer:/cheers/" + reference,
      platformType,
      chatId,
    },
  };
  return sendMessage(params);
}

/**
 * 生成邮件内容
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回生成结果
 */
export function generateEmailContent({
  chatId,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.genemail.content",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
    },
  };
  return sendMessage(params);
}

/**
 * 发送邮件消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.cover - 邮件封面
 * @param {string} options.subject - 邮件主题
 * @param {string} options.text - 邮件内容
 * @param {Array} options.attachments - 附件列表
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendEmailMessage({
  chatId,
  cover,
  subject,
  text,
  attachments,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.emails",
    timestamp,
    requestId: requestId,
    messageStatus: 0,
    data: {
      platformType,
      chatId,
      cover,
      subject,
      text,
      attachments,
    },
  };
  return sendMessage(params);
}

/**
 * 发送表情消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.text - 表情文本
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendEmojiMessage({
  chatId,
  text,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.smiles",
    timestamp,
    requestId: requestId,
    data: {
      platformType,
      chatId,
      text,
    },
  };
  return sendMessage(params);
}

/**
 * 发送贴纸消息
 * @param {Object} options - 配置选项
 * @param {string} options.chatId - 聊天ID
 * @param {string} options.reference - 贴纸引用
 * @param {string} options.text - 贴纸文本
 * @param {string} [options.platformType='dating'] - 平台类型
 * @param {number} options.timestamp - 时间戳
 * @param {string} [options.requestId] - 请求ID
 * @returns {Promise} 返回发送结果
 */
export function sendStickerMessage({
  chatId,
  reference,
  text,
  platformType = "dating",
  timestamp,
  requestId,
}) {
  const params = {
    events: "chat.post.stickers",
    timestamp,
    requestId: requestId,
    data: {
      reference,
      platformType,
      chatId,
      text,
    },
  };
  return sendMessage(params);
}

// 私有登录获取历史聊天列表，使用socket方式
export function getDatingHistoryList(param, requestId) {
  const params = {
    events: "chat.get.chats",
    data: {
      pageNum: param.pageNum,
      pageSize: param.pageSize,
      isFavorite: param.isFavorite || "",
      platformUserId: param.platformUserId || "",
      platformType: "dating",
      searchValue: param.searchValue || "",
      isUnAnswer: param.isUnAnswer || "", // 1为未读
    },
    requestId,
  };
  return sendMessage(params);
}
// 收藏
export function sendFavoriteMessage({
  chatId,
  platformUserId,
  userId,
  isFavorite,
  platformType = "dating",
  requestId,
}) {
  const params = {
    events: "chat.post.favorite",
    requestId: requestId,
    data: {
      chatId,
      platformUserId,
      userId,
      platformType,
      isFavorite,
    },
  };
  return sendMessage(params);
}

// 删除对话
export function deleteChat({
  chatId,
  platformUserId,
  userId,
  platformType = "dating",
  requestId,
}) {
  const params = {
    events: "chat.user.delete",
    requestId: requestId,
    data: {
      chatId,
      platformUserId,
      userId,
      platformType,
    },
  };
  return sendMessage(params);
}

// 获取用户信息
export function getUserDetail({
  platformUserId,
  platformType = "dating",
  requestId,
}) {
  const params = {
    events: "get.user.detail",
    requestId: requestId,
    data: {
      platformUserId,
      platformType,
    },
  };
  return sendMessage(params);
}
// 上传
// {
//   "events": "post.upload.resources",
//   "requestId": "123123",
//   "data": {
//       "sendFiles": [
//           {
//               "fileType": "photo",
//               "fileUrl": "https://oss.lwvvl.com/mkf/2025/07/14/0897b708faf34dbea458eaa54381d338.jpg"
//           }
//       ],
//       "platformUserId": "49266959942",
//       "userId": "50866854742"
//   }
// }
// 通过本地客户端上传资源到dating
export function sendDatingMediaPrivate({
  sendFiles,
  platformUserId,
  userId,
  requestId,
}) {
  const params = {
    events: "post.upload.resources",
    requestId: requestId,
    data: {
      sendFiles,
      platformUserId,
      userId,
    },
  };
  return sendMessage(params);
}
// 检测未回复
export function checkUnanswer({ platformUserId, userId }) {
  const params = {
    events: "chat.check.unanswer",
    data: {
      platformUserId,
      userId,
    },
  };
  return sendMessage(params);
}
